import Vue from "vue";
import { stripTimestampAndExtension } from "@/assets/js/Utilities";
import { customLutsStore } from "@/services/indexedDB.service";
import { toString } from "lodash";

const getDefaultState = () => {
    return {
        customLuts: [],
        folder: {
            id: null,
            name: null,
            files: [],
        },
        importProgressTotal: 0,
        importProgress: [],
        totalImportSize: 0,
        lutToDelete: {
            id: null,
            name: null
        },
        lutToRename: {
            id: null,
            name: null
        },
    };
}

const UserFilesCustomLuts = {
    namespaced: true,

    state: getDefaultState(),
    mutations: {
        SET_CUSTOM_LUTS: (state, payload) => {
            state.customLuts = payload;
        },
        SET_CUSTOM_LUTS_FOLDER: (state, payload) => {
            state.folder = payload;
        },
        SET_IMPORT_PROGRESS_TOTAL: (state, payload) => {
            state.importProgressTotal = payload;
        },
        SET_TOTAL_IMPORT_SIZE: (state, payload) => {
            state.totalImportSize = payload;
        },
        SET_IMPORT_PROGRESS: (state, payload) => {
            state.importProgress[payload.key] = payload.value;
        },
        CLEAR_IMPORT_PROGRESS: state => {
            state.importProgress = [];
        },
        SET_LUT_TO_DELETE: (state, payload) => {
            state.lutToDelete = payload;
        },
        SET_LUT_TO_RENAME: (state, payload) => {
            state.lutToRename = payload;
        }
    },

    actions: {
        index: ({ commit, state, rootState, getters }) => {
            return new Promise((resolve, reject) => {
                Vue.prototype.$apiService
                    .get(`accounts/${rootState.User.account.id}/custom-luts`)
                    .then(({ data }) => {
                        data.forEach(folder => {
                            folder.files = getters.lutsListSorted(folder.files);
                        })

                        data = getters.foldersListSorted(data);

                        commit("SET_CUSTOM_LUTS", data);

                        const folder = getters.folder(state.folder.id);
                        if (folder) commit("SET_CUSTOM_LUTS_FOLDER", folder);

                        window._Adjustments.customLUT.refreshAllBlocks();
                        window._Adjustments.CustomLutV2.refreshAllBlocks();

                        resolve(data);
                    })
                    .catch(err => {
                        console.log(err);
                        reject(err);
                    });
            });
        },

        fetch: async ({ commit, dispatch }, folder) => {
            const list = [];

            for await (const file of folder.files) {
                let storeData = await customLutsStore.getItem(toString(file.id));

                if (!storeData) {
                    const blob = await dispatch("Files/fetchFromS3", {
                        url: file.presignedURL,
                        config: {
                            responseType: "blob",
                        }
                    }, { root: true });

                    storeData = {
                        id: file.id,
                        folderId: folder.id,
                        name: file.name,
                        file: new File([blob], file.name),
                    };

                    await customLutsStore.setItem(file.id, storeData)
                }

                list.push(storeData);
            }

            return list;
        },

        createFolder: ({ commit, rootState }, name) => {
            return new Promise(resolve => {
                Vue.prototype.$apiService
                    .post(`accounts/${rootState.User.account.id}/custom-luts/folders`, { name })
                    .then(response => {
                        commit("SET_CUSTOM_LUTS_FOLDER", response.data);
                        resolve(response.data)
                    })
            })
        },

        renameFolder: ({ commit, rootState }, payload) => {
            return Vue.prototype.$apiService
                .patch(`accounts/${rootState.User.account.id}/custom-luts/folders/${payload.folderId}`, {
                    name: payload.name
                })
                .then(response => {
                    return response.data;
                })
                .catch(err => {
                    console.log(err.response);
                    return err;
                });
        },

        create: ({ rootState, state }, payload) => {
            let formData = new FormData();
            payload.files.forEach((file, key) => {
                formData.append(`files[${key}][name]`, file.name);
                formData.append(`files[${key}][size]`, file.size);
            });

            return Vue.prototype.$apiService
                .post(`accounts/${rootState.User.account.id}/custom-luts/folders/${payload.folder_id}`, formData)
                .then(response => {
                    return response.data;
                })
                .catch(err => {
                    console.log(err.response);
                    return err;
                });
        },

        uploadToS3: ({ state, dispatch, commit }, payload) => {
            const config = {
                onUploadProgress: progressEvent => {
                    commit("SET_IMPORT_PROGRESS", {
                        key: payload.key,
                        value: progressEvent.loaded,
                    });

                    let totalImagesUploaded = 0;
                    state.importProgress.forEach(progress => {
                        totalImagesUploaded += progress;
                    });

                    let percentCompleted = Math.round((totalImagesUploaded / state.totalImportSize) * 100);
                    commit("SET_IMPORT_PROGRESS_TOTAL", percentCompleted);
                },
            };

            return Vue.prototype.$S3Service
                .put(payload.url, payload.file, config)
                .then(response => {
                    return response;
                })
                .catch(err => {
                    console.log(err.response);
                    return err;
                });
        },

        deleteFolder({ rootState }, folder_id) {
            return Vue.prototype.$apiService
                .delete(`accounts/${rootState.User.account.id}/custom-luts/folders/${folder_id}`)
                .then(response => {
                    return response.data;
                })
                .catch(err => {
                    console.log(err.response);
                    return err;
                });
        },

        delete: ({ rootState }, file_id) => {
            return Vue.prototype.$apiService
                .delete(`accounts/${rootState.User.account.id}/custom-luts/files/${file_id}`)
                .then(response => {
                    return response.data;
                })
                .catch(err => {
                    console.log(err.response);
                    return err;
                });
        },

        rename: ({ rootState }, payload) => {
            return Vue.prototype.$apiService
                .patch(`accounts/${rootState.User.account.id}/custom-luts/files/${payload.fileId}`, {
                    name: payload.name
                })
                .then(response => {
                    return response.data;
                })
                .catch(err => {
                    console.log(err.response);
                    return err;
                });
        }

    },

    getters: {
        folder: state => id => {
            let foundFolder = {
                id: null,
                name: null,
                files: []
            }

            state.customLuts.find(folder => {
                if (folder.id == id) foundFolder = folder;
            })

            return foundFolder;
        },

        lut: state => id => {
            let lut = {
                id: null,
                name: null,
            }

            state.customLuts.forEach(folder => {
                folder.files.forEach(file => {
                    if (file.id == id) lut = file;
                });
            })

            return lut;
        },

        folderByLutId: state => id => {
            let foundFolder = {
                id: null,
                name: null,
                files: []
            }

            state.customLuts.forEach(folder => {
                folder.files.forEach(file => {
                    if (file.id == id) foundFolder = folder;
                });
            })

            return foundFolder
        },

        lutsListSorted: state => list => {
            return list.sort((a, b) => {
                if (stripTimestampAndExtension(a.name) < stripTimestampAndExtension(b.name)) return -1;
                if (stripTimestampAndExtension(a.name) > stripTimestampAndExtension(b.name)) return 1;
                return 0
            })
        },

        foldersListSorted: state => list => {
            return list.sort((a, b) => a.name.localeCompare(b.name))
        }
    }
}

export default UserFilesCustomLuts;
